// Quotes + e-signature: product price book, quotes built from deal line
// items, public accept/sign page, DRAFT-invoice handoff on acceptance.
//
// MONEY IS CENT-MATH: every amount crossing the wire is an integer count of
// cents (unitPriceCents, subtotalCents, ...) and tax rates are basis points
// (taxRateBps, 850 = 8.5%). The UI formats with Intl.NumberFormat(cents/100)
// and parses dollar inputs with Math.round(parseFloat * 100). Totals are
// always recomputed server-side; clients never send computed totals.

export type QuoteStatus =
  | 'DRAFT'
  | 'SENT'
  | 'VIEWED'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'EXPIRED'
  | 'VOID'

export const QUOTE_STATUSES: QuoteStatus[] = [
  'DRAFT',
  'SENT',
  'VIEWED',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'VOID',
]

export const QUOTE_STATUS_DISPLAY_NAMES: Record<QuoteStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  VOID: 'Void',
}

/** Hex chip colors (same palette as INVOICE_STATUS_COLORS). */
export const QUOTE_STATUS_COLORS: Record<QuoteStatus, string> = {
  DRAFT: '#6b7280',
  SENT: '#3b82f6',
  VIEWED: '#f59e0b',
  ACCEPTED: '#10b981',
  DECLINED: '#ef4444',
  EXPIRED: '#9ca3af',
  VOID: '#64748b',
}

export type QuoteEventType =
  | 'CREATED'
  | 'SENT'
  | 'VIEWED'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'VOIDED'
  | 'INVOICE_CREATED'
  | 'CONTRACT_CREATED'

// ---------------------------------------------------------------------------
// Products (price book) — its own module; referenced by quote AND invoice
// line items. Lines snapshot name/price/tax at pick time and freeze.
// ---------------------------------------------------------------------------

/** Mirrors accounting-software item types (QBO Service/Inventory/NonInventory) */
export type ProductItemType = 'SERVICE' | 'PRODUCT' | 'NON_INVENTORY'

export const PRODUCT_ITEM_TYPES: ProductItemType[] = [
  'SERVICE',
  'PRODUCT',
  'NON_INVENTORY',
]

export interface ProductDTO {
  id: string
  organizationId: string
  name: string
  sku: string | null
  description: string | null
  /** Integer cents (12999 = $129.99) */
  unitPriceCents: number
  currency: string
  isActive: boolean
  itemType: ProductItemType
  isTaxable: boolean
  /** Default line tax in basis points (850 = 8.5%); applied at pick time */
  taxRateBps: number
  incomeAccountCode: string | null
  /** Accounting-app sync identity ('QUICKBOOKS'); null = created in Scrambl */
  externalSystem: string | null
  externalId: string | null
  /** Optional buy-side link: the inventory item this product prices */
  inventoryItemId: string | null
  /** Hydrated display name for the linked inventory item (list endpoint) */
  inventoryItemName?: string | null
  /** Read-through on-hand quantity from the linked stock item; null when the
   *  product is not stock-tracked (list endpoint) */
  stockQuantity?: number | null
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateProductDTO {
  name: string
  sku?: string
  description?: string
  unitPriceCents: number
  currency?: string
  isActive?: boolean
  itemType?: ProductItemType
  isTaxable?: boolean
  taxRateBps?: number
  incomeAccountCode?: string | null
  inventoryItemId?: string | null
}

export interface UpdateProductDTO {
  name?: string
  sku?: string | null
  description?: string | null
  unitPriceCents?: number
  currency?: string
  isActive?: boolean
  itemType?: ProductItemType
  isTaxable?: boolean
  taxRateBps?: number
  incomeAccountCode?: string | null
  inventoryItemId?: string | null
}

/** One quote/invoice appearance of a product (detail-page Sales section). */
export interface ProductUsageLineDTO {
  /** Parent record id (quote or invoice) */
  recordId: string
  /** 'quote' | 'invoice' */
  kind: 'quote' | 'invoice'
  /** Display label — 'Q-0042 · Website redesign' / 'INV-2041 · March retainer' */
  label: string
  status: string
  quantity: number
  /** Line total in integer cents (invoice dollars are converted) */
  amountCents: number
  /** ISO date of the parent record (quote createdAt / invoice issueDate) */
  date: string
}

export interface ProductUsageDTO {
  product: ProductDTO
  /** Lifetime units on non-void invoices */
  unitsInvoiced: number
  /** Lifetime revenue in integer cents on non-void invoices */
  revenueInvoicedCents: number
  quoteCount: number
  invoiceCount: number
  /** Most recent appearances, quotes + invoices interleaved, newest first */
  recent: ProductUsageLineDTO[]
}

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

export interface QuoteLineItemDTO {
  id: string
  quoteId: string
  /** NULL = custom line (not from the price book) */
  productId: string | null
  name: string
  description: string | null
  quantity: number
  unitPriceCents: number
  /** quantity * unitPriceCents — server-computed */
  amountCents: number
  order: number
}

/** Replace-all payload for the line-item editor (order = array index). */
export interface QuoteLineItemInput {
  productId?: string | null
  name: string
  description?: string
  quantity: number
  unitPriceCents: number
}

export interface QuoteEventDTO {
  id: string
  type: QuoteEventType
  detail: string | null
  occurredAt: string
}

export interface QuoteDTO {
  id: string
  organizationId: string
  /** Per-org sequence; display as Q-0042 (padded to 4) */
  number: number
  name: string
  status: QuoteStatus
  dealId: string | null
  contactId: string | null
  entityId: string | null
  currency: string
  subtotalCents: number
  discountCents: number
  taxCents: number
  totalCents: number
  taxRateBps: number
  notes: string | null
  terms: string | null
  validUntil: string | null
  publicToken: string
  sentAt: string | null
  viewedAt: string | null
  respondedAt: string | null
  signerName: string | null
  signerEmail: string | null
  signatureText: string | null
  declineReason: string | null
  /** DRAFT invoice created on accept (when the quote has a client entity) */
  invoiceId: string | null
  /**
   * Derived, detail endpoint only: the contract on this quote's deal (a deal
   * has at most one contract). Null on list rows and unconverted deals.
   */
  contractId: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
  lineItems: QuoteLineItemDTO[]
  events: QuoteEventDTO[]
  // Hydrated display names (detail endpoint only; null when unset/deleted)
  dealName?: string | null
  contactName?: string | null
  contactEmail?: string | null
  entityName?: string | null
}

/** Lightweight list row (no line items / events). */
export type QuoteListItemDTO = Omit<QuoteDTO, 'lineItems' | 'events'>

/** Optional list filters; omitted fields mean "all". */
export interface ListQuotesQuery {
  dealId?: string
  entityId?: string
}

/** Server-sortable quote columns (validated allow-list — never raw input). */
export const QUOTE_SORT_FIELDS = [
  'number',
  'name',
  'status',
  'totalCents',
  'subtotalCents',
  'validUntil',
  'sentAt',
  'viewedAt',
  'respondedAt',
  'signerName',
  'createdAt',
  'updatedAt',
] as const

export type QuoteSortField = (typeof QUOTE_SORT_FIELDS)[number]

/**
 * Table-mode list query: presence of page/limit switches GET /quotes from the
 * legacy bare array to the { data, meta } envelope (deployments precedent).
 */
export interface ListQuotesTableQuery extends ListQuotesQuery {
  page?: number
  limit?: number
  search?: string
  /** Comma-joined on the wire; validated against QuoteStatus server-side */
  statuses?: QuoteStatus[]
  /** Multi-select client filter (comma-joined on the wire) */
  entityIds?: string[]
  /** yyyy-MM-dd date-only strings, inclusive */
  createdFrom?: string
  createdTo?: string
  sortBy?: QuoteSortField
  sortOrder?: 'asc' | 'desc'
}

/** KPI tiles above the quotes table — computed org-wide, not per page. */
export interface QuoteStatsDTO {
  /** SENT + VIEWED quotes awaiting a signature */
  openCount: number
  /** Sum of totalCents across open quotes */
  openValueCents: number
  /** Currency of the open pipeline (first open quote's; 'usd' fallback) */
  openCurrency: string
  acceptedCount: number
  declinedCount: number
}

export interface CreateQuoteDTO {
  name: string
  dealId?: string
  contactId?: string
  entityId?: string
}

/**
 * Metadata edits are allowed on OPEN quotes (DRAFT/SENT/VIEWED/EXPIRED);
 * ACCEPTED/DECLINED/VOID are immutable (the e-signature / decline / void is
 * the record). Money stays DRAFT-only: currency here, line items through
 * UpdateQuoteLineItemsDTO.
 *
 * `status` is a manual transition (table status field): timestamps and quote
 * events are applied server-side; marking ACCEPTED this way records no
 * signature and does NOT run the invoice-on-accept handoff (that belongs to
 * the signer flow).
 */
export interface UpdateQuoteDTO {
  name?: string
  status?: QuoteStatus
  dealId?: string | null
  contactId?: string | null
  entityId?: string | null
  currency?: string
  notes?: string | null
  terms?: string | null
  /** ISO datetime or null to clear */
  validUntil?: string | null
}

/** Replace-all line items + pricing knobs; totals recomputed server-side. */
export interface UpdateQuoteLineItemsDTO {
  items: QuoteLineItemInput[]
  discountCents?: number
  taxRateBps?: number
}

export interface SendQuoteDTO {
  /** Defaults to the linked contact's email when omitted */
  recipientEmail?: string
}

// ---------------------------------------------------------------------------
// Public (recipient-facing) page
// ---------------------------------------------------------------------------

export interface PublicQuoteLineItemDTO {
  name: string
  description: string | null
  quantity: number
  unitPriceCents: number
  amountCents: number
}

export interface PublicQuoteDTO {
  organizationName: string
  number: number
  name: string
  status: QuoteStatus
  currency: string
  subtotalCents: number
  discountCents: number
  taxCents: number
  totalCents: number
  taxRateBps: number
  notes: string | null
  terms: string | null
  validUntil: string | null
  respondedAt: string | null
  signerName: string | null
  declineReason: string | null
  lineItems: PublicQuoteLineItemDTO[]
}

export interface PublicAcceptQuoteDTO {
  signerName: string
  signerEmail: string
  /** Typed full-name signature — the e-signature of record */
  signatureText: string
}

export interface PublicDeclineQuoteDTO {
  reason?: string
}
